/**
 * CosturiCentralizator - Tabel centralizator costuri personal
 * Tabel cu toate cheltuielile per angajat (CAS, CASS, Impozit, Salariu Net, Tichete, Clauza, Retineri, etc.)
 */
import { useState, useEffect, memo, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Loader2, FileDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS_RO = [
  { value: 1, label: 'Ianuarie' }, { value: 2, label: 'Februarie' },
  { value: 3, label: 'Martie' }, { value: 4, label: 'Aprilie' },
  { value: 5, label: 'Mai' }, { value: 6, label: 'Iunie' },
  { value: 7, label: 'Iulie' }, { value: 8, label: 'August' },
  { value: 9, label: 'Septembrie' }, { value: 10, label: 'Octombrie' },
  { value: 11, label: 'Noiembrie' }, { value: 12, label: 'Decembrie' },
];

const CosturiCentralizator = memo(function CosturiCentralizator({ token }) {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('lunar');
  const [data, setData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [nameFilter, setNameFilter] = useState('');

  const luna = `${year}-${String(month).padStart(2, '0')}`;

  const filteredData = (data || []).filter(row =>
    !nameFilter || row.name.toLowerCase().includes(nameFilter.toLowerCase())
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = viewMode === 'lunar' ? `luna=${luna}` : `an=${year}`;
      const res = await fetch(`${API_URL}/api/salary-adapter/costuri-personal?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setData(d.rows || []);
        setColumns(d.columns || []);
      } else {
        setData([]);
      }
    } catch { setData([]); }
    finally { setLoading(false); }
  }, [token, luna, year, viewMode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const params = viewMode === 'lunar' ? `luna=${luna}` : `an=${year}`;
      const res = await fetch(`${API_URL}/api/salary-adapter/costuri-personal/export-${type}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const label = viewMode === 'lunar' ? `${MONTHS_RO[month - 1]?.label}_${year}` : `${year}`;
        a.download = `Costuri_Personal_${label}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${type.toUpperCase()} exportat`);
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Eroare export');
      }
    } catch { toast.error('Eroare de rețea'); }
    finally { setExporting(null); }
  };

  const formatNum = (n) => (n || 0).toLocaleString('ro-RO');

  const totals = columns.reduce((acc, col) => {
    acc[col.key] = filteredData.reduce((s, r) => s + (r[col.key] || 0), 0);
    return acc;
  }, {});
  totals.venit_angajat = filteredData.reduce((s, r) => s + (r.venit_angajat || 0), 0);
  totals.total_cheltuiala = filteredData.reduce((s, r) => s + (r.total_cheltuiala || 0), 0);

  return (
    <div className="space-y-4" data-testid="costuri-centralizator">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('lunar')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'lunar' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="costuri-view-lunar"
            >
              Lunar
            </button>
            <button
              onClick={() => setViewMode('anual')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'anual' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="costuri-view-anual"
            >
              Anual
            </button>
          </div>

          {/* Month/Year nav */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                if (viewMode === 'lunar') {
                  if (month === 1) { setMonth(12); setYear(y => y - 1); }
                  else setMonth(m => m - 1);
                } else {
                  setYear(y => y - 1);
                }
              }}
              className="p-1.5 rounded hover:bg-slate-100"
              data-testid="costuri-nav-prev"
            >
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            {viewMode === 'lunar' && (
              <select
                value={month}
                onChange={e => setMonth(Number(e.target.value))}
                className="text-sm border rounded px-2 py-1"
                data-testid="costuri-month-select"
              >
                {MONTHS_RO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            )}
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="text-sm border rounded px-2 py-1"
              data-testid="costuri-year-select"
            >
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              onClick={() => {
                if (viewMode === 'lunar') {
                  if (month === 12) { setMonth(1); setYear(y => y + 1); }
                  else setMonth(m => m + 1);
                } else {
                  setYear(y => y + 1);
                }
              }}
              className="p-1.5 rounded hover:bg-slate-100"
              data-testid="costuri-nav-next"
            >
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          {/* Name filter */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
              placeholder="Caută angajat..."
              className="pl-8 pr-3 py-1.5 text-xs border rounded-lg w-44 focus:outline-none focus:ring-1 focus:ring-blue-300"
              data-testid="costuri-name-filter"
            />
          </div>
        </div>

        {/* Export buttons */}
        {data && data.length > 0 && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} disabled={!!exporting} data-testid="export-costuri-pdf">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleExport('excel')} disabled={!!exporting} data-testid="export-costuri-excel">
              {exporting === 'excel' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}Excel
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {viewMode === 'lunar'
              ? `Costuri Personal - ${MONTHS_RO[month - 1]?.label} ${year}`
              : `Costuri Personal - Anul ${year}`
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : !data || data.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p className="text-sm">Nu exista date pentru {viewMode === 'lunar' ? `${MONTHS_RO[month - 1]?.label} ${year}` : `anul ${year}`}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="costuri-personal-table">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 w-8">Nr.</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 sticky left-8 bg-slate-50 z-10 min-w-[160px]">Angajat</th>
                    {columns.map(col => (
                      <th key={col.key} className="px-2 py-2 text-right font-semibold text-slate-600 whitespace-nowrap min-w-[80px]">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-2 py-2 text-right font-bold text-blue-700 whitespace-nowrap min-w-[100px] bg-blue-50">Venit Angajat</th>
                    <th className="px-2 py-2 text-right font-bold text-slate-700 whitespace-nowrap min-w-[110px]">Total Cheltuială cu Angajat</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, i) => (
                    <tr key={row.cnp || i} className="border-b hover:bg-slate-50/50">
                      <td className="px-2 py-1.5 text-slate-500 sticky left-0 bg-white z-10">{i + 1}</td>
                      <td className="px-2 py-1.5 font-medium text-slate-800 sticky left-8 bg-white z-10">{row.name}</td>
                      {columns.map(col => (
                        <td key={col.key} className={`px-2 py-1.5 text-right tabular-nums ${
                          col.source === 'placeholder' ? 'text-slate-300' : 'text-slate-700'
                        }`}>
                          {formatNum(row[col.key])}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-right font-bold text-blue-700 tabular-nums bg-blue-50/30">{formatNum(row.venit_angajat)}</td>
                      <td className="px-2 py-1.5 text-right font-bold text-slate-800 tabular-nums">{formatNum(row.total_cheltuiala)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300">
                    <td className="px-2 py-2 sticky left-0 bg-slate-100 z-10"></td>
                    <td className="px-2 py-2 font-bold text-slate-800 sticky left-8 bg-slate-100 z-10">TOTAL</td>
                    {columns.map(col => (
                      <td key={col.key} className="px-2 py-2 text-right font-bold text-slate-800 tabular-nums">
                        {formatNum(totals[col.key])}
                      </td>
                    ))}
                    <td className="px-2 py-2 text-right font-bold text-blue-700 tabular-nums bg-blue-50/50">{formatNum(totals.venit_angajat)}</td>
                    <td className="px-2 py-2 text-right font-bold text-slate-900 tabular-nums">{formatNum(totals.total_cheltuiala)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default CosturiCentralizator;
